/**
 * Tenant permission catalog. Roles store a list of these keys.
 * Groups and labels drive the permission-matrix UI.
 */
export const PERMISSION_GROUPS = Object.freeze([
  {
    key: 'pos',
    label: 'Billing',
    permissions: [
      { key: 'pos.bill', label: 'Create bills' },
      { key: 'pos.hold', label: 'Hold / resume bills' },
      { key: 'pos.discount', label: 'Give discounts (within limit)' },
      { key: 'pos.void', label: 'Void / cancel bills' },
      { key: 'pos.refund', label: 'Refund bills' },
      { key: 'pos.reprint', label: 'Reprint receipts' },
      { key: 'pos.open_drawer', label: 'Open cash drawer' },
    ],
  },
  {
    key: 'menu',
    label: 'Menu',
    permissions: [
      { key: 'menu.edit', label: 'Edit menu & prices' },
      { key: 'menu.availability', label: 'Mark items out of stock' },
    ],
  },
  {
    key: 'stock',
    label: 'Inventory',
    permissions: [
      { key: 'stock.view', label: 'View stock' },
      { key: 'stock.manage', label: 'Purchases, suppliers, transfers' },
      { key: 'stock.adjust', label: 'Stock adjustments & wastage' },
    ],
  },
  {
    key: 'cash',
    label: 'Shifts & cash',
    permissions: [
      { key: 'shift.manage', label: 'Open / close shifts and day' },
      { key: 'cash.inout', label: 'Cash in / out entries' },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    permissions: [
      { key: 'reports.view', label: 'View reports & dashboard' },
      { key: 'reports.export', label: 'Export reports' },
    ],
  },
  {
    key: 'customers',
    label: 'Customers',
    permissions: [
      { key: 'customers.view', label: 'View customers' },
      { key: 'customers.manage', label: 'Edit customers & loyalty' },
    ],
  },
  {
    key: 'kitchen',
    label: 'Kitchen',
    permissions: [{ key: 'kds.use', label: 'Use kitchen display' }],
  },
  {
    key: 'admin',
    label: 'Administration',
    permissions: [
      { key: 'staff.manage', label: 'Manage staff users & PINs' },
      { key: 'roles.manage', label: 'Manage roles & permissions' },
      { key: 'devices.manage', label: 'Register / revoke devices' },
      { key: 'outlets.manage', label: 'Manage outlets' },
      { key: 'settings.manage', label: 'Business settings' },
      { key: 'printers.manage', label: 'Printers' },
      { key: 'audit.view', label: 'View audit log' },
    ],
  },
]);

export const PERMISSIONS = Object.freeze(PERMISSION_GROUPS.flatMap((g) => g.permissions.map((p) => p.key)));

const MANAGER = [
  'pos.bill',
  'pos.hold',
  'pos.discount',
  'pos.void',
  'pos.refund',
  'pos.reprint',
  'pos.open_drawer',
  'menu.edit',
  'menu.availability',
  'stock.view',
  'stock.manage',
  'stock.adjust',
  'shift.manage',
  'cash.inout',
  'reports.view',
  'reports.export',
  'customers.view',
  'customers.manage',
  'kds.use',
  'staff.manage',
  'devices.manage',
  'printers.manage',
  'audit.view',
];

export const DEFAULT_ROLE_PERMISSIONS = Object.freeze({
  owner: PERMISSIONS,
  manager: MANAGER,
  cashier: ['pos.bill', 'pos.hold', 'pos.discount', 'pos.reprint', 'menu.availability', 'customers.view', 'cash.inout'],
  kitchen: ['kds.use', 'menu.availability'],
});

export const DEFAULT_ROLE_LIMITS = Object.freeze({
  owner: { maxDiscountPct: 100, maxDiscountPaise: null },
  manager: { maxDiscountPct: 50, maxDiscountPaise: null },
  cashier: { maxDiscountPct: 10, maxDiscountPaise: 10000 },
  kitchen: { maxDiscountPct: 0, maxDiscountPaise: 0 },
});
